import { Fragment, forwardRef } from 'react';

import { TableCell, TableCellProps } from '@material-ui/core';

import { isSameDay } from 'date-fns';
import * as duration from 'duration-fns';

import {
  MetadataText,
  formatDateFuture,
  formatDuration,
} from '@spotify/confidence-core-react';
import {
  RolloutDataScheduleScheduleStepsStatus,
  RolloutDataScheduleState,
  RolloutListItemFragment,
} from '@spotify/confidence-graphql';
import {
  DraftReadinessText,
  EndedRuntimeText,
  usePlanningDates,
} from '@spotify/plugin-confidence-workflows-module-planning';
import { ProgressBar } from '@spotify/plugin-confidence-workflows-react';

interface ProgressCellProps extends TableCellProps {
  workflowInstance: Pick<
    RolloutListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}

export const ProgressCell = forwardRef<HTMLTableCellElement, ProgressCellProps>(
  ({ workflowInstance, ...props }, ref) => {
    const { state, checks, moduleData2 } = workflowInstance;
    const { plannedStartDate, actualRuntime } = usePlanningDates(
      moduleData2.planning,
    );

    return (
      <TableCell {...props} ref={ref}>
        {state === 'draft' && (
          <DraftReadinessText
            checks={checks}
            plannedStartDate={plannedStartDate}
          />
        )}
        {state === 'live' && (
          <LiveProgress workflowInstance={workflowInstance} />
        )}
        {(state === 'ended' || (state === 'archived' && actualRuntime)) && (
          <EndedRuntimeText label="Finished" actualRuntime={actualRuntime} />
        )}
        {state === 'archived' && !actualRuntime && (
          <MetadataText items={['Archived']} />
        )}
      </TableCell>
    );
  },
);

const formatNumber = (number: number) => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number);
};

function LiveProgress({
  workflowInstance,
}: {
  workflowInstance: Pick<RolloutListItemFragment, 'moduleData2'>;
}) {
  const { actualStartDate, currentRuntime } = usePlanningDates(
    workflowInstance.moduleData2.planning,
  );

  const exposure = workflowInstance.moduleData2?.exposure?.exposure?.reduce(
    (acc, curr) => acc + (Number(curr.totalExposureCount) || 0),
    0,
  );

  const isRampingUp =
    workflowInstance.moduleData2?.rollout?.schedule?.state ===
    RolloutDataScheduleState.Enabled;

  const nextIndex =
    workflowInstance.moduleData2?.rollout?.schedule?.steps.findIndex(
      step => step.status !== RolloutDataScheduleScheduleStepsStatus.Succeeded,
    );

  const prevStep =
    nextIndex !== undefined && nextIndex > 0 - 1
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps[nextIndex - 1]
      : null;

  const prevReach =
    prevStep?.status === RolloutDataScheduleScheduleStepsStatus.Succeeded
      ? Number(prevStep.reach) * 100
      : null;

  const nextStep =
    nextIndex !== undefined && nextIndex !== -1
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps[nextIndex]
      : null;

  const nextReach =
    nextStep?.status === RolloutDataScheduleScheduleStepsStatus.Pending
      ? Number(nextStep.reach) * 100
      : null;

  const steps =
    nextStep &&
    nextIndex !== undefined &&
    workflowInstance.moduleData2?.rollout?.schedule
      ? workflowInstance.moduleData2?.rollout?.schedule?.steps.slice(
          0,
          nextIndex + 1,
        )
      : [];

  const scheduleRuntime = steps.reduce(
    (acc, step) =>
      duration.sum(acc, duration.parse(step.durationSincePreviousStep)),
    {},
  );

  const nextDate = actualStartDate
    ? duration.apply(actualStartDate, scheduleRuntime)
    : null;

  const progress = workflowInstance.moduleData2?.rollout?.reach
    ? Number(workflowInstance.moduleData2?.rollout?.reach) * 100
    : null;

  // If there a schedule, it has failed, and we're still at the same reach
  const isAutomaticallyFailed =
    workflowInstance.moduleData2?.rollout?.schedule?.state ===
      RolloutDataScheduleState.Failed &&
    progress !== null &&
    progress === prevReach;

  const renderText = () => {
    if (
      nextDate &&
      nextReach &&
      isRampingUp &&
      isSameDay(nextDate, new Date())
    ) {
      // example: 80% at 10:22 / 80% tomorrow
      return `${nextReach}% ${formatDateFuture(nextDate)}`;
    }

    if (isAutomaticallyFailed) {
      return 'Schedule stopped';
    }

    if (exposure !== undefined) {
      if (exposure === 0) return 'None exposed yet';
      return `${formatNumber(exposure)} exposed`;
    }

    return null;
  };

  return (
    <Fragment>
      {progress !== null && (
        <Fragment>
          <ProgressBar
            variant="determinate"
            value={progress}
            color="secondary"
          />
          <MetadataText items={[`${progress}%`, renderText()]} />
        </Fragment>
      )}

      {progress === null && (
        <MetadataText items={[formatDuration(currentRuntime)]} />
      )}
    </Fragment>
  );
}
